/**
 * Who the picker shows when nobody has been picked.
 *
 * cg_order_owner() in the database resolves owner_id → created_by → the linked
 * project's owner, and that resolution is what decides where a client's
 * rejection lands. The picker has to say the same thing, or somebody looks at
 * an empty dropdown, assumes rejections go nowhere, and either assigns an
 * owner that was not needed or stops trusting the notification.
 *
 * This mirrors the first two links of that chain. The third — the linked
 * project's owner — is deliberately NOT mirrored: the browser would have to
 * fetch a project to display a placeholder, and being wrong in the direction
 * of "we could not work it out" is honest, where guessing a name would not be.
 */

export interface OwnerCandidate {
  id: string;
  label: string;
}

export interface OwnerSource {
  owner_id: string | null;
  created_by: string | null;
  project_id: string | null;
}

export type OwnerResolution =
  /** Somebody was picked. */
  | { kind: 'explicit'; id: string; label: string }
  /** Nobody picked, but created_by will receive rejections. */
  | { kind: 'fallback'; id: string; label: string }
  /** Nothing here resolves it, but the linked project might. */
  | { kind: 'project' }
  /** Nobody at all — the trigger will tell the whole CG team. */
  | { kind: 'none' };

function labelFor(people: OwnerCandidate[], id: string): string {
  return people.find((person) => person.id === id)?.label ?? '';
}

export function resolveOwner(order: OwnerSource, people: OwnerCandidate[]): OwnerResolution {
  if (order.owner_id !== null && order.owner_id !== '') {
    return { kind: 'explicit', id: order.owner_id, label: labelFor(people, order.owner_id) };
  }
  if (order.created_by !== null && order.created_by !== '') {
    const label = labelFor(people, order.created_by);
    // An id with no name attached is somebody who has left, or an account this
    // person cannot read. Showing a bare uuid would be worse than admitting we
    // do not know, so it degrades to the project branch's wording.
    if (label === '') {
      return { kind: 'project' };
    }
    return { kind: 'fallback', id: order.created_by, label };
  }
  if (order.project_id !== null && order.project_id !== '') {
    return { kind: 'project' };
  }
  return { kind: 'none' };
}

/**
 * The muted line under the picker.
 *
 * Says where a rejection goes RIGHT NOW, which is the only question somebody
 * looking at an unassigned order actually has. The team fallback is named
 * explicitly because "nobody" is the answer people fear and it is not the
 * answer — the trigger tells everyone on CG rather than dropping it.
 */
export function ownerHint(resolution: OwnerResolution): string {
  if (resolution.kind === 'explicit') {
    return '';
  }
  if (resolution.kind === 'fallback') {
    return `Unassigned — client rejections go to ${resolution.label}, who created this order.`;
  }
  if (resolution.kind === 'project') {
    return "Unassigned — client rejections follow this order's linked project owner.";
  }
  return 'Unassigned — client rejections go to everyone on the CG team.';
}

/** The value the select should show. Empty means the "Unassigned" option. */
export function ownerSelectValue(order: OwnerSource): string {
  return order.owner_id ?? '';
}
